import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, MessageCircle, Heart, Music } from 'lucide-react';
import { Button } from './ui/button';

const BETA_WELCOME_KEY = 'twintube_beta_welcome_seen';

const BetaWelcomePopup = () => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Don't show on admin page
    if (window.location.pathname === '/admin') return;
    // Check if user has seen the welcome popup
    const hasSeenWelcome = localStorage.getItem(BETA_WELCOME_KEY);
    if (!hasSeenWelcome) {
      // Small delay before showing popup for better UX
      const timer = setTimeout(() => setIsOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleClose = () => {
    localStorage.setItem(BETA_WELCOME_KEY, 'true');
    setIsOpen(false);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="relative max-w-md w-full bg-gradient-to-br from-card via-card to-purple-950/20 border border-purple-500/30 rounded-2xl shadow-2xl shadow-purple-500/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative sparkle elements */}
            <div className="absolute top-4 right-12 opacity-30">
              <Sparkles size={40} className="text-purple-500" />
            </div>
            <div className="absolute bottom-8 left-4 opacity-20">
              <Music size={30} className="text-purple-400" />
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X size={18} />
            </button>

            {/* Content */}
            <div className="p-6 pt-8">
              {/* Header with icon */}
              <div className="flex justify-center mb-4">
                <div className="relative">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/30">
                    <Sparkles size={32} className="text-white" />
                  </div>
                  <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold border-2 border-card">
                    β
                  </div>
                </div>
              </div>

              {/* Title */}
              <h2 className="text-2xl font-bold text-center mb-2">
                Welcome, Early Supporter!
              </h2>

              {/* Message */}
              <p className="text-center text-muted-foreground mb-4">
                Thank you for being one of our first users and beta testers! Your support means everything to us.
              </p>

              {/* Features list */}
              <div className="space-y-2 mb-6 bg-white/5 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <Heart size={16} className="text-pink-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    We're constantly making improvements to bring you the best Twin Cities music experience.
                  </p>
                </div>
                <div className="flex items-start gap-2">
                  <MessageCircle size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-muted-foreground">
                    We'd love to hear your feedback and community suggestions!
                  </p>
                </div>
              </div>

              {/* Action buttons */}
              <div className="space-y-2">
                <Button 
                  className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  onClick={handleClose}
                >
                  <Music size={16} className="mr-2" />
                  Start Exploring
                </Button>
                <Button 
                  variant="outline"
                  className="w-full border-purple-500/50 hover:bg-purple-500/10"
                  onClick={() => {
                    handleClose();
                    window.location.href = '/contact';
                  }}
                >
                  <MessageCircle size={16} className="mr-2" />
                  Send Feedback
                </Button>
              </div>

              {/* Footer */}
              <p className="text-xs text-muted-foreground mt-4 flex items-center justify-center gap-1">
                <Sparkles size={12} className="text-purple-500" />
                Supporting Good Vibes in the Twin Cities
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default BetaWelcomePopup;
